import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

const languages = [
  { code: 'pt', label: 'Português', path: '' },
  { code: 'de', label: 'Deutsch', path: '/de' },
  { code: 'en', label: 'English', path: '/en' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  const handleLangChange = (lang: typeof languages[0]) => {
    i18n.changeLanguage(lang.code);
    const currentPath = window.location.pathname;
    const cleanPath = currentPath.replace(/^\/de/, '').replace(/^\/en/, '');
    const newPath = lang.path + cleanPath;
    window.location.href = newPath || '/';
  };

  const navLinks = [
    { label: t('nav.home'), href: currentLang.path + '/' },
    { label: t('nav.guides'), href: currentLang.path + '/cssbuy-spreadsheet' },
    { label: t('nav.review'), href: currentLang.path + '/article/cssbuy-review' },
    { label: t('nav.shipping'), href: currentLang.path + '/article/cssbuy-shipping-cost' },
    { label: t('nav.safety'), href: currentLang.path + '/article/cssbuy-safety-guide' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50 border-b border-background-200">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href={currentLang.path + '/'} className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-file-list-3-line text-lg md:text-xl"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-semibold text-foreground-900">
              CSSBuy Spreadsheet
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right side: Shopping Guide CTA + Language + Mobile menu */}
          <div className="flex items-center gap-2">
            <a
              href={currentLang.path + '/cssbuy-spreadsheet'}
              className="hidden md:inline-flex btn-primary text-sm px-4 py-2"
            >
              <i className="ri-shopping-bag-line mr-1.5"></i>
              {t('nav.shoppingGuide')}
            </a>

            {/* Language Dropdown */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors"
              >
                <i className="ri-global-line"></i>
                <span className="hidden sm:inline">{currentLang.label}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-44 rounded-lg bg-background-50 border border-background-200 shadow-lg overflow-hidden">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { handleLangChange(lang); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        i18n.language === lang.code
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-md text-foreground-800 hover:bg-background-100 transition-colors"
            >
              <i className={`${isOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 py-4 max-h-[80vh] overflow-y-auto">
          {/* Primary nav */}
          <div className="flex flex-col gap-1 mb-3">
            <a
              href={currentLang.path + '/'}
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100"
            >
              <i className="ri-home-line text-base"></i>
              {t('nav.home')}
            </a>
            <a
              href={currentLang.path + '/cssbuy-spreadsheet'}
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-2 px-4 py-3 rounded-md text-sm font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100"
            >
              <i className="ri-shopping-bag-line text-base"></i>
              {t('nav.shoppingGuide')}
            </a>
            {navLinks.filter(l => l.label !== t('nav.home')).map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Categories */}
          <div className="border-t border-background-200 pt-2">
            <button
              onClick={() => setCatsOpen(!catsOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-md"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line text-base"></i>
                {t('nav.categories')}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${catsOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {catsOpen && (
              <div className="grid grid-cols-2 gap-1 mt-1 mb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100"
                  >
                    <span className="w-6 h-6 flex items-center justify-center text-primary-500">
                      <i className={cat.icon}></i>
                    </span>
                    <span className="truncate">{t(`categories.${cat.nameKey}`)}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Language */}
          <div className="border-t border-background-200 pt-2 mt-1">
            <p className="px-4 text-xs font-medium text-foreground-500 mb-1 mt-1">{t('nav.language')}</p>
            <div className="flex gap-2 px-4 pb-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { handleLangChange(lang); setIsOpen(false); }}
                  className={`px-4 py-2 text-sm rounded-full transition-colors ${
                    i18n.language === lang.code
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}